<template>
  <header class="header_in clearfix" :class="headerClasses">
      <div class="row header_in__row no-gutters">
        <div class="header_in__brand">
          <div id="logo" class="header_in__logo">
            <router-link to="/" class="header_in__logo-link">
              <span class="header_in__logo-badge">
                <img
                  :src="logoUrl"
                  class="logo"
                  :alt="brandName"
                />
              </span>
              <span v-if="!compactBrand" class="header_in__brand-wordmark">
                <span class="header_in__brand-name">{{ brandName }}</span>
                <span class="header_in__brand-tag">{{ brandTagline }}</span>
              </span>
            </router-link>
          </div>
        </div>
        <div class="header_in__center">
          <router-link
            v-if="showHomeButton"
            :to="homePath"
            class="header_in__home"
            aria-label="Home"
            title="Home"
            @click.native="onHomeClick"
          >
            <i class="fas fa-home" aria-hidden="true"></i>
            <span class="header_in__home-text">Home</span>
          </router-link>
          <PortalShortcuts v-if="showPortalShortcuts" />
          <div v-if="$slots.default" class="header_in__center-extra">
            <slot></slot>
          </div>
        </div>
        <div class="header_in__side header_in__side--right">
          <KotNotifications />
          <button
            type="button"
            class="header_in__theme"
            :title="dark ? 'Switch to light mode' : 'Switch to dark mode'"
            :aria-label="dark ? 'Switch to light mode' : 'Switch to dark mode'"
            @click="onToggleDark"
          >
            <i :class="dark ? 'fas fa-sun' : 'fas fa-moon'" aria-hidden="true"></i>
          </button>
          <AccountMenu
            v-if="status"
            class="header_in__user"
            :name="displayName"
            @logout="signout"
          >
            <template v-slot="{ toggle, open }">
            <button
              type="button"
              id="sign-out"
              class="header_in__user-chip"
              :title="displayName"
              aria-label="Account menu"
              aria-haspopup="menu"
              :aria-expanded="open ? 'true' : 'false'"
              @click="toggle"
            >
              <span class="header_in__user-text">
                <span class="header_in__user-name">{{ displayName }}</span>
              </span>
              <span class="header_in__avatar" aria-hidden="true">
                {{ userInitials }}
              </span>
            </button>
            </template>
          </AccountMenu>
        </div>
      </div>
  </header>
</template>

<script>
import {mapGetters, mapState} from 'vuex';
import PortalShortcuts from '../PortalShortcuts/index.vue';
import AccountMenu from '../AccountMenu/index.vue';
import KotNotifications from '../KotNotifications/index.vue';
import { USER_TYPES } from '@/utils/permissions';
import { isKotDark, toggleKotDark } from '@/utils/kotDarkMode';

export default {
  props: {
    classes: {
      type: String,
      default: '',
    },
    hidePortalShortcuts: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    PortalShortcuts,
    AccountMenu,
    KotNotifications,
  },
  data() {
    return {
      dark: false,
    };
  },
  methods: {
    onToggleDark() {
      this.dark = toggleKotDark();
    },
    signout() {
      this.$store.dispatch('auth/LogOut');
      this.$router.push('/login');
    },
    onHomeClick(e) {
      if (this.homePath !== '/waiter') return;
      this.$emit('home');
      if (this.$route.path === '/waiter') {
        e.preventDefault();
      }
    },
  },
  mounted() {
    this.dark = isKotDark();
    if (!this.status) {
      this.$router.push('/login');
      return;
    }
    this.$store.dispatch('organization/fetchOrganization');
  },
  computed: {
    ...mapState('auth', ['status', 'user']),
    ...mapGetters('auth', ['GetUser']),
    ...mapGetters('organization', ['logoUrl', 'brandName', 'brandTagline']),
    showPortalShortcuts() {
      if (this.hidePortalShortcuts) return false;
      const path = this.$route && this.$route.path;
      return path !== '/login';
    },
    showHomeButton() {
      const path = this.$route && this.$route.path;
      return path && path !== '/login';
    },
    homePath() {
      if (Number(this.user && this.user.usertype) === USER_TYPES.WAITER) {
        return '/waiter';
      }
      return '/';
    },
    compactBrand() {
      const path = (this.$route && this.$route.path) || '';
      return path.startsWith('/rooms-booking');
    },
    headerClasses() {
      const extra = this.compactBrand ? 'header_in--booking' : '';
      return [this.classes, extra].filter(Boolean).join(' ');
    },
    username() {
      const user = this.GetUser;
      if (user && user.username) return user.username;
      return "";
    },
    displayName() {
      if (this.username) {
        return this.username;
      }
      return 'Guest';
    },
    userInitials() {
      const user = this.GetUser;
      if (user) {
        const name = (user.username || "").trim();
        if (name.length >= 2) return name.slice(0, 2).toUpperCase();
        if (name) return name.charAt(0).toUpperCase();
      }
      const name = (this.displayName || '').trim();
      if (!name || name === 'Guest') {
        return 'G';
      }
      const parts = name.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
      }
      return name.slice(0, 2).toUpperCase();
    },
  },
};
</script>

<style>
header.header_in.header_in {
  overflow: visible !important;
}

.header_in {
  --header-bar-h: 60px;
  --header-bg: #ffffff;
  --logo-badge-bg: #ffffff;
  --logo-badge-border: #e2e8f0;
  position: fixed;
  z-index: 14;
  margin-bottom: 0;
  height: var(--header-bar-h);
  padding: 0 16px 0 0;
  box-sizing: border-box;
  overflow: visible;
  background: var(--header-bg);
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
}

header.header_in.order-place-header {
  position: relative;
  top: auto;
  left: auto;
  right: auto;
  width: 100%;
  flex-shrink: 0;
  z-index: 10;
  border-bottom: none;
  box-shadow: none;
}

header.header_in.header_in--booking .header_in__logo-link {
  max-width: 56px;
}

header.header_in.header_in--booking .header_in__logo-badge {
  width: 44px;
  height: 44px;
}

header.header_in.header_in--booking .header_in__brand {
  padding-right: 8px;
}

.header_in > .row,
.header_in__row {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  height: var(--header-bar-h);
  min-height: var(--header-bar-h);
  margin: 0;
  width: 100%;
}

.header_in__row {
  position: relative;
  z-index: 1;
}

.header_in__brand {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  height: 100%;
  padding: 4px 12px 4px 16px;
  box-sizing: border-box;
}

header.header_in #logo.header_in__logo {
  float: none;
  margin: 0;
  display: flex;
  align-items: center;
  height: 100%;
  line-height: 0;
  overflow: visible;
}

.header_in__logo-link {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  height: 100%;
  max-width: 300px;
  line-height: 1.1;
  text-decoration: none;
}

.header_in__logo-badge {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  padding: 4px;
  border-radius: 10px;
  background: var(--logo-badge-bg);
  border: 1px solid var(--logo-badge-border);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
  box-sizing: border-box;
}

.header_in__brand-wordmark {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  min-width: 0;
  text-align: left;
}

.header_in__brand-name {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.05;
  color: var(--brand-primary, #512d82);
  letter-spacing: -0.02em;
  font-family: 'Poppins', 'Segoe UI', sans-serif;
}

.header_in__brand-tag {
  margin-top: 1px;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #64748b;
}

.header_in__theme {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin-right: 8px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #1e293b;
  cursor: pointer;
}

.header_in__user {
  display: flex;
  align-items: center;
  gap: 10px;
}

.header_in__user-chip {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  max-width: 260px;
  min-height: 40px;
  padding: 4px 5px 4px 16px;
  border-radius: 999px;
  border: 1px solid var(--brand-primary-border);
  background: #ffffff;
  box-shadow: none;
  cursor: pointer;
  font: inherit;
  color: inherit;
  transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.header_in__user-chip:hover {
  background: var(--brand-primary-muted);
  border-color: var(--brand-primary-border);
  box-shadow: 0 1px 3px var(--brand-shadow-sm);
}

#sign-out.header_in__user-chip {
  color: inherit;
}

.header_in__user-text {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex: 1 1 auto;
  min-width: 0;
  line-height: 1.2;
}

.header_in__user-name {
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 600;
  color: var(--brand-primary);
  letter-spacing: -0.01em;
}

.header_in__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--brand-primary);
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.02em;
}

.header_in__user-chip:hover .header_in__avatar {
  background: var(--brand-primary-hover);
}

.header_in__center {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 0 12px 0 8px;
}

.header_in__home {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border: 1px solid #1a6d78;
  border-radius: 10px;
  background: #ffffff;
  color: #1a6d78 !important;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
}

.header_in__home:hover {
  background: rgba(26, 109, 120, 0.08);
  border-color: #1a6d78;
  box-shadow: 0 2px 6px rgba(26, 109, 120, 0.16);
  text-decoration: none;
}

.header_in__home i {
  font-size: 14px;
  line-height: 1;
}

.header_in__center-extra {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
  flex: 0 1 auto;
}

.header_in__side--right {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-left: 4px;
  padding: 0 12px 0 14px;
  border-left: 1px solid #e2e8f0;
}

.pb-18 {
  padding-bottom: 0 !important;
}

header.header_in .logo {
  display: block;
  width: 100%;
  height: 100%;
  max-width: 44px;
  max-height: 44px;
  min-width: 0;
  object-fit: contain;
  object-position: center center;
  margin: 0 auto;
  padding: 0;
  position: static;
  top: auto;
  transform: none;
  filter: none;
}

@media (max-width: 640px) {
  .header_in__brand-wordmark {
    display: none;
  }

  .header_in__logo-link {
    max-width: 56px;
  }

  .header_in__home-text {
    display: none;
  }

  .header_in__home {
    width: 36px;
    padding: 0;
  }

  .header_in__user-name {
    max-width: 88px;
    font-size: 12px;
  }

  .header_in__user-chip {
    max-width: 180px;
    min-height: 36px;
    padding: 3px 4px 3px 12px;
    gap: 8px;
  }

  .header_in__avatar {
    width: 30px;
    height: 30px;
    font-size: 11px;
  }
}
</style>
